import { useCallback } from "react";

import { CountryCode, parsePhoneNumberFromString } from "libphonenumber-js";

import { formatPhoneNumber, getPhoneNumberValue } from "./utils";

export interface PhoneInputContext {
  effectiveCountry?: CountryCode;
  /**
   * Whether the country is controlled by the parent via countryCode prop.
   * When true, auto-detection is disabled.
   */
  isCountryControlled: boolean;
}

export interface PhoneNextState {
  display: string;
  detectedCountry?: CountryCode;
  valid: boolean;
}

/**
 * Pure helper: parse and format a phone input, optionally detecting country from +E.164 numbers.
 */
export const computeNextPhoneState = (raw: string, ctx: PhoneInputContext): PhoneNextState => {
  const { effectiveCountry, isCountryControlled } = ctx;

  const startsWithPlus = raw.trim().startsWith("+");

  // Always compute a formatted display string using our formatter (which falls back to AsYouType)
  const formatted = formatPhoneNumber(raw, effectiveCountry);

  // Parse the raw input to assess validity and possibly detect country
  const parseCountryHint = startsWithPlus ? undefined : isCountryControlled ? effectiveCountry : undefined;
  // Special-case DSN/military style prefixes like "94+3125551234" where the two-digit
  // prefix (e.g. 94) is not part of the E.164 number. In that case parse the portion
  // after the plus for validity and possible detection.
  const dsnMatch = raw.trim().match(/^(\d{2})\+(.+)$/);

  let detectedCountry: CountryCode | undefined;
  let valid = false;

  if (dsnMatch) {
    const phonePart = dsnMatch[2];
    const parsedPhone = parseCountryHint ? parsePhoneNumberFromString(phonePart, parseCountryHint) : parsePhoneNumberFromString(phonePart);

    // For DSN we don't change the display (formatPhoneNumber already preserves the prefix),
    // but the validity should be determined by the phone portion after the +.
    valid = parsedPhone?.isValid() ?? false;

    // Only auto-detect country for explicit +E.164 inputs; keep DSN detection conservative.
    if ((!isCountryControlled || startsWithPlus) && startsWithPlus && parsedPhone?.country) {
      detectedCountry = parsedPhone.country;
    }
  } else {
    const parsed = parseCountryHint ? parsePhoneNumberFromString(raw, parseCountryHint) : parsePhoneNumberFromString(raw);

    // Auto-detect country from +E.164 numbers when in uncontrolled mode, or when user explicitly types +
    if ((!isCountryControlled || startsWithPlus) && startsWithPlus && parsed?.country) {
      detectedCountry = parsed.country;
    }

    valid = parsed?.isValid() ?? false;
  }

  return { display: formatted, detectedCountry, valid };
};

/**
 * Internal hook to apply phone input: parse, format, emit onChange, and optionally auto-detect country.
 */
export function usePhoneInput(options: {
  context: PhoneInputContext;
  prevDisplay: string | undefined;
  setDisplay: (next: string, prev: string) => void;
  onChange?: (value: string, prevValue: string) => void;
  onCountryDetected?: (country: CountryCode) => void;
}) {
  const { context, prevDisplay, setDisplay, onChange, onCountryDetected } = options;

  const applyPhoneInputValue = useCallback(
    (raw: string) => {
      const next = computeNextPhoneState(raw, context);

      if (next.detectedCountry) {
        onCountryDetected?.(next.detectedCountry);
      }

      const prevValue = getPhoneNumberValue(prevDisplay ?? "");

      const nextValue = next.valid ? getPhoneNumberValue(next.display) : raw;

      const phoneWithCountry = context.effectiveCountry
        ? parsePhoneNumberFromString(nextValue, context.effectiveCountry)
        : parsePhoneNumberFromString(nextValue);

      setDisplay(next.display, prevDisplay ?? "");
      if (phoneWithCountry) {
        onChange?.(phoneWithCountry.number, prevValue);
      }
    },
    [context, prevDisplay, setDisplay, onChange, onCountryDetected],
  );

  return { applyPhoneInputValue };
}
